import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { Spinner, toast } from "@performyard/py-components";

import {
	VIDEO_CONFERENCING_PROVIDER_CONFIG,
	isVideoConferencingProvider,
} from "../../../../components/VideoConferencing";
import { CalendarSection } from "./components/CalendarSection";
import { MeetingsIntegrationsHeader } from "./components/MeetingsIntegrationsHeader";
import { VideoConferencingSection } from "./components/VideoConferencingSection";

import ErrorPage from "../../../../pages/Error/ErrorPage";

import {
	useMeetingsIntegrationsSettings,
	useVideoConferencingProviderFlags,
} from "../../../../api/hooks/defs/meetings-integrations";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

import { CALENDAR_PROVIDER_DISPLAY_NAMES, CalendarProvider } from "./config";

// Status queries aren't pre-fetched: their endpoints 403 when a section's FF/admin toggle is off.
export const meetingsIntegrationsViewLoader = () => {
	return dataLoader([[currentUserQuery]]);
};

const isCalendarProvider = (value: string): value is CalendarProvider => value === "google" || value === "outlook";

const resolveDisplayName = (providerParam: string | null): string => {
	if (!providerParam) {
		return "integration";
	}
	if (isCalendarProvider(providerParam)) {
		return CALENDAR_PROVIDER_DISPLAY_NAMES[providerParam];
	}
	if (isVideoConferencingProvider(providerParam)) {
		return VIDEO_CONFERENCING_PROVIDER_CONFIG[providerParam].longName;
	}
	return providerParam;
};

export const MeetingsIntegrationsView = (): React.ReactElement => {
	const [searchParams, setSearchParams] = useSearchParams();
	const queryClient = useQueryClient();
	// Tracks the last handled success/provider combo to keep the effect idempotent.
	const handledOAuthResultRef = useRef<string | null>(null);

	const { value: ffCalendarIntegration, query: calendarFFQuery } = useFeatureFlagQuery(
		"ff-is-calendar-integration-enabled",
		false
	);
	const { value: ffMeetingsIntegrations, query: meetingsFFQuery } = useFeatureFlagQuery(
		"ff-is-meetings-integrations-enabled",
		false
	);
	const { anyEnabled: anyVideoProviderFFOn } = useVideoConferencingProviderFlags();

	const anyIntegrationFFOn = ffCalendarIntegration || (ffMeetingsIntegrations && anyVideoProviderFFOn);

	const { data: meetingsIntegrationsSettings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({
		enabled: anyIntegrationFFOn,
	});

	const isCalendarSectionVisible = ffCalendarIntegration && !!meetingsIntegrationsSettings?.calendar?.enabled;
	const isVideoConferencingSectionVisible =
		ffMeetingsIntegrations && anyVideoProviderFFOn && !!meetingsIntegrationsSettings?.video_conferencing?.enabled;

	// Unified OAuth success/failure toast for calendar + video conferencing callbacks.
	useEffect(() => {
		const successParam = searchParams.get("success");
		const providerParam = searchParams.get("provider");
		const reasonParam = searchParams.get("reason");

		if (!successParam && !providerParam) {
			return;
		}

		const resultKey = `${successParam ?? ""}|${providerParam ?? ""}|${reasonParam ?? ""}`;
		if (handledOAuthResultRef.current === resultKey) {
			return;
		}
		handledOAuthResultRef.current = resultKey;

		if (successParam) {
			const displayName = resolveDisplayName(providerParam);
			if (successParam === "true") {
				toast.success(`${displayName} successfully connected!`);
				if (providerParam && isCalendarProvider(providerParam)) {
					queryClient.invalidateQueries({ queryKey: ["user-calendar-integration-status"] });
				} else if (providerParam && isVideoConferencingProvider(providerParam)) {
					queryClient.invalidateQueries({ queryKey: ["user-video-conferencing-status"] });
				}
			} else if (successParam === "false") {
				// Provider-specific connect-failure copy (e.g. Teams' admin-consent hint) is centralized
				// in config; fall back to a generic retry message when a provider defines none.
				const providerConfig =
					providerParam && isVideoConferencingProvider(providerParam)
						? VIDEO_CONFERENCING_PROVIDER_CONFIG[providerParam]
						: undefined;
				const providerFailureMessage =
					(reasonParam === "unlicensed" ? providerConfig?.connectUnlicensedMessage : undefined) ??
					providerConfig?.connectFailureMessage;
				toast.error(providerFailureMessage ?? `Failed to connect ${displayName}. Please try again.`);
			}
		}

		// Construct a fresh URLSearchParams — mutating the tracked object causes re-render bugs.
		const next = new URLSearchParams(searchParams);
		next.delete("success");
		next.delete("provider");
		next.delete("reason");
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams, queryClient]);

	const isLoadingGating = calendarFFQuery.isLoading || meetingsFFQuery.isLoading || isSettingsLoading;

	if (isLoadingGating) {
		return (
			<div className="flex h-[208px] w-full">
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			</div>
		);
	}

	if (!isCalendarSectionVisible && !isVideoConferencingSectionVisible) {
		return <ErrorPage code={403} />;
	}

	return (
		<div className="flex w-full flex-col gap-6">
			<MeetingsIntegrationsHeader />
			{isCalendarSectionVisible && <CalendarSection />}
			{isVideoConferencingSectionVisible && <VideoConferencingSection />}
		</div>
	);
};
